import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, Settings, Save, TestTube, CheckCircle, 
  AlertCircle, Link as LinkIcon, Key, Lock 
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const CRMSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall, isAdmin } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  
  const [crmConfig, setCrmConfig] = useState({
    crm_name: '',
    api_url: '',
    api_key: '',
    api_secret: '',
    webhook_url: '',
    enabled: false
  });
  
  const [connectionStatus, setConnectionStatus] = useState({
    tested: false,
    success: false,
    message: ''
  });

  useEffect(() => {
    if (isAdmin()) {
      fetchCRMSettings();
    }
  }, []);

  const fetchCRMSettings = async () => {
    try {
      setLoading(true);
      // In production, this would fetch from backend
      // For now, load from localStorage
      const saved = localStorage.getItem('crm_settings');
      if (saved) {
        setCrmConfig(JSON.parse(saved));
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load CRM settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!crmConfig.api_url || !crmConfig.api_key) {
      toast({
        title: 'Missing Information',
        description: 'Please enter API URL and API Key',
        variant: 'destructive'
      });
      return;
    }

    try {
      setTesting(true);
      
      // Simulate API test - In production, this would call backend
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock success
      setConnectionStatus({
        tested: true,
        success: true,
        message: 'Connection successful! CRM is reachable.'
      });
      
      toast({
        title: 'Connection Successful',
        description: 'Your CRM API is working correctly'
      });
    } catch (error) {
      setConnectionStatus({
        tested: true,
        success: false,
        message: 'Connection failed. Please check your credentials.'
      });
      
      toast({
        title: 'Connection Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    if (!crmConfig.api_url || !crmConfig.api_key) {
      toast({
        title: 'Missing Information',
        description: 'Please enter at least API URL and API Key',
        variant: 'destructive'
      });
      return;
    }
    
    setConfirmDialog(true);
  };

  const confirmSave = async () => {
    try {
      setSaving(true);
      
      // In production, this would save to backend
      // For now, save to localStorage
      localStorage.setItem('crm_settings', JSON.stringify(crmConfig));
      
      toast({
        title: 'Settings Saved',
        description: 'CRM configuration has been saved successfully'
      });
      
      setConfirmDialog(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin()) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">Only administrators can access CRM settings.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading settings...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/customers')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <Settings className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">CRM Integration Settings</h1>
            <p className="text-gray-600">Configure your CRM connection for customer sync</p>
          </div>
        </div>

        {/* Connection Status */}
        {connectionStatus.tested && (
          <Card className={connectionStatus.success ? 'border-green-500' : 'border-red-500'}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {connectionStatus.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${connectionStatus.success ? 'text-green-900' : 'text-red-900'}`}>
                    {connectionStatus.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Popular CRM Integrations */}
        <Card>
          <CardHeader>
            <CardTitle>Popular CRM Platforms</CardTitle>
            <CardDescription>Quick setup guides for common CRM systems</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['Salesforce', 'HubSpot', 'Zoho CRM'].map((crm) => (
                <Button
                  key={crm}
                  variant="outline"
                  className="h-auto py-4"
                  onClick={() => setCrmConfig({ ...crmConfig, crm_name: crm })}
                >
                  <div className="text-center">
                    <p className="font-semibold">{crm}</p>
                    <p className="text-xs text-gray-600">Click to select</p>
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CRM Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>API Configuration</CardTitle>
            <CardDescription>Enter your CRM API credentials</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="crm-name">CRM Name (Optional)</Label>
              <Input
                id="crm-name"
                placeholder="e.g., Salesforce, HubSpot, Custom CRM"
                value={crmConfig.crm_name}
                onChange={(e) => setCrmConfig({ ...crmConfig, crm_name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-url">
                <LinkIcon className="h-4 w-4 inline mr-2" />
                API Base URL *
              </Label>
              <Input
                id="api-url"
                placeholder="https://api.yourcrm.com/v1"
                value={crmConfig.api_url}
                onChange={(e) => setCrmConfig({ ...crmConfig, api_url: e.target.value })}
              />
              <p className="text-xs text-gray-500">Full URL including protocol (https://)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-key">
                <Key className="h-4 w-4 inline mr-2" />
                API Key *
              </Label>
              <Input
                id="api-key"
                type="password"
                placeholder="Enter your API key"
                value={crmConfig.api_key}
                onChange={(e) => setCrmConfig({ ...crmConfig, api_key: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-secret">
                <Lock className="h-4 w-4 inline mr-2" />
                API Secret (Optional)
              </Label>
              <Input
                id="api-secret"
                type="password"
                placeholder="Enter API secret if required"
                value={crmConfig.api_secret}
                onChange={(e) => setCrmConfig({ ...crmConfig, api_secret: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhook-url">Webhook URL (Optional)</Label>
              <Input
                id="webhook-url"
                placeholder="https://yourcrm.com/webhooks/customers"
                value={crmConfig.webhook_url}
                onChange={(e) => setCrmConfig({ ...crmConfig, webhook_url: e.target.value })}
              />
              <p className="text-xs text-gray-500">URL to receive customer updates</p>
            </div>

            <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
              <input
                type="checkbox"
                id="enable-crm"
                checked={crmConfig.enabled}
                onChange={(e) => setCrmConfig({ ...crmConfig, enabled: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="enable-crm" className="cursor-pointer">
                Enable CRM sync for new customers
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Setup Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-600">
            <div className="flex gap-2">
              <Badge>1</Badge>
              <p>Obtain API credentials from your CRM admin panel</p>
            </div>
            <div className="flex gap-2">
              <Badge>2</Badge>
              <p>Enter the API URL and credentials in the form above</p>
            </div>
            <div className="flex gap-2">
              <Badge>3</Badge>
              <p>Click "Test Connection" to verify your credentials</p>
            </div>
            <div className="flex gap-2">
              <Badge>4</Badge>
              <p>Save the settings to enable CRM integration</p>
            </div>
            <div className="flex gap-2">
              <Badge>5</Badge>
              <p>New customers will automatically sync to your CRM</p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={handleTestConnection}
            disabled={testing}
          >
            <TestTube className="h-4 w-4 mr-2" />
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm CRM Settings</DialogTitle>
            <DialogDescription>
              Are you sure you want to save these CRM settings? This will affect how customer data is synced.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">CRM Name:</span>
              <span className="font-medium">{crmConfig.crm_name || 'Not specified'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">API URL:</span>
              <span className="font-medium truncate ml-4">{crmConfig.api_url}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Sync Enabled:</span>
              <span className="font-medium">{crmConfig.enabled ? 'Yes' : 'No'}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmSave} disabled={saving}>
              {saving ? 'Saving...' : 'Yes, Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CRMSettings;
